import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  ConfigurationDTO,
  CustomerDTO,
  DepartmentDTO,
  OnlineMbeConfigurationDTO,
  OnlineMbeCourierVisibilityDto,
  OnlineMBEExtraServiceDto,
  OnlineMbePackTypeDto,
  OnlineMbeShipmentTemplateDto,
  OnlineMBEThermicConfigDto
} from "../model/data.model";
import {
  CourierVisibilityDTO,
  CustomerDetailsDef,
  CustomerPaymentsDef,
  CustomerSettingsDef
} from "../model/component.model";
import store from "../vuex/store";
import { getKeyValueConfig } from "@/utils/fetchKeyValueConfigLevel";

export class CustomerService {
  customer: CustomerDTO;

  saveCustomer(
    customerMbeId: string,
    customerDto: CustomerDTO
  ): Promise<CustomerDTO> {
    const config = {
      url: store.getters.BASE_API_URL + "/customers/" + customerMbeId,
      method: "POST",
      data: { customer: customerDto }
    } as AxiosRequestConfig;
    return axios.request(config).then((res: AxiosResponse<CustomerDTO>) => {
      this.customer = res.data;
      return res.data;
    });
  }

  getCustomerDetails(): CustomerDetailsDef {
    return {} as CustomerDetailsDef;
  }

  getCustomerPayments(): CustomerPaymentsDef {
    return {} as CustomerPaymentsDef;
  }

  getCustomerCosts(): void {
    throw "unimplemented";
  }

  getCustomerPriceList(): void {
    throw "unimplemented";
  }

  getCustomerSettings(): CustomerSettingsDef {
    return {} as CustomerSettingsDef;
  }

  saveCustomerPayments(customerPayments: CustomerPaymentsDef): Promise<any> {
    return this.saveCustomer("", {} as CustomerDTO);
  }

  saveCustomerSettings(customerSettings: CustomerSettingsDef): Promise<any> {
    return this.saveCustomer("", {} as CustomerDTO);
  }

  deleteCustomer(customerMbeId: string) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: store.getters.BASE_API_URL + "/customers/" + customerMbeId
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  static getOnlineMBECourierVisibility(store: any) {
    const config = {
      url:
        store.getters.BASE_API_URL +
        `/customers/${store.getters["customer/customerMbeId"]}/onlinembe/couriervisibilityconfig/${store.getters.user.username}`,
      method: "GET"
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<OnlineMbeCourierVisibilityDto[]>) => {
        return res.data as OnlineMbeCourierVisibilityDto[];
      });
  }

  static loadCustomerInfo(store: any) {
    return CustomerService.getCustomerInfo(
      store.getters["customer/customerMbeId"]
    ).then(res => {
      store.commit("customer/setCustomerInfo", res);
      store.commit(
        "updateCustomerQaplaEnabled",
        res.custConfiguration.enableQapla
      );
    });
  }

  static getCustomerInfo(customerMbeId: string): Promise<CustomerDTO> {
    return axios
      .get(store.getters.BASE_API_URL + "/customers/" + customerMbeId)
      .then(res => res.data);
  }

  static getCustomerPackTypes(): Promise<OnlineMbePackTypeDto[]> {
    const username = store.getters.user.username;
    const customerMbeId = store.getters["customer/info"].customerId;
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerMbeId +
          "/onlinembe/packType/" +
          username
      )
      .then(res => res.data)
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  static getCourierVisibility(): Promise<CourierVisibilityDTO[]> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/customers/onlinembe/custom-duties/courier-visibility`
    };
    return axios
      .request(config)
      .then(res => res.data as CourierVisibilityDTO[]);
  }

  static updateCourierVisibility(
    data: CourierVisibilityDTO
  ): Promise<CourierVisibilityDTO> {
    const config: AxiosRequestConfig = {
      method: "PUT",
      url: `${store.getters.BASE_API_URL}/customers/onlinembe/custom-duties`,
      data
    };
    return axios.request(config).then(res => res.data as CourierVisibilityDTO);
  }

  static loadCustomerConfig(store: any): Promise<OnlineMbeConfigurationDTO> {
    return CustomerService.getCustomerConfig(
      store.getters["storeSelected"].idEntity,
      store.getters["user"].username
    ).then((res: OnlineMbeConfigurationDTO) => {
      store.commit("customer/setCustomerConfig", res);
      return res;
    });
  }

  static getCustomerConfig(
    customerId: number,
    username: string
  ): Promise<OnlineMbeConfigurationDTO> {
    return axios
      .get(
        store.getters.BASE_API_URL +
          `/customers/${customerId}/onlinembe/generalconfig`,
        {
          params: {
            username: username
          }
        }
      )
      .then(res => res.data)
      .catch(err => {
        console.log("ERROR: GET customer config with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static saveCustomerConfig(
    customerId: number,
    onlineMbeConfigurationDTO: OnlineMbeConfigurationDTO
  ): Promise<OnlineMbeConfigurationDTO> {
    const config = {
      url:
        store.getters.BASE_API_URL +
        `/customers/${customerId}/onlinembe/generalconfig/${onlineMbeConfigurationDTO.onlineMbeConfigurationId}`,
      method: "PUT",
      data: onlineMbeConfigurationDTO
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<OnlineMbeConfigurationDTO>) => {
        return res.data;
      });
  }

  static loadCustomerDepartments(
    store: any,
    no_department_text: string,
    page = 0,
    departmentBusinessName?: string
  ) {
    const isDepartmentMandatory =
      store.getters["customer/config"].isDepartmentMandatory;
    const onlySelectedDepartments =
      store.getters["customer/config"].onlySelectedDepartments;
    const onlineMbeConfigurationId =
      store.getters["customer/config"].onlineMbeConfigurationId;
    return CustomerService.getCustomerDepartments(
      store.getters["customer/customerMbeId"],
      page,
      departmentBusinessName,
      onlySelectedDepartments,
      onlineMbeConfigurationId
    ).then(res => {
      if (!isDepartmentMandatory) {
        res.unshift({
          departmentId: 0,
          departmentBusinessName: no_department_text
        } as DepartmentDTO);
      }
      const defaultDepartmentId = store.getters["customer/config"].departmentId;

      if (onlySelectedDepartments !== undefined && onlySelectedDepartments) {
        store.commit("customer/setDepartments", res);
      } else {
        if (defaultDepartmentId !== 0 && defaultDepartmentId !== undefined) {
          let found = false;
          res.forEach((department: DepartmentDTO) => {
            if (department.departmentId === defaultDepartmentId) {
              found = true;
            }
          });
          if (!found) {
            this.readDepartment(defaultDepartmentId).then((resDefault: any) => {
              res.push(resDefault);
              store.commit("customer/setDepartments", res);
            });
          } else {
            store.commit("customer/setDepartments", res);
          }
        } else {
          store.commit("customer/setDepartments", res);
        }
      }
    });
  }
  static readDepartment(departmentId: number): Promise<DepartmentDTO> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + `/customers/departments/${departmentId}`
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  static getCustomerDepartments(
    customerMbeId: string,
    page = 0,
    departmentBusinessName?: string,
    onlySelectedDepartments?: boolean,
    onlineMbeConfigurationId?: number
  ): Promise<DepartmentDTO[]> {
    const config: AxiosRequestConfig = {
      method: "GET",
      params: {
        page: page,
        size: 50,
        departmentBusinessName: departmentBusinessName,
        viewOnlySelected: onlySelectedDepartments,
        onlineMbeConfigurationId: onlineMbeConfigurationId
      },
      url: `${store.getters.BASE_API_URL}/customers/${customerMbeId}/departments`
    };
    return axios.request(config).then(res => res.data.content);
  }

  static loadCustomerDefaultStoreName(store: any) {
    return CustomerService.getCustomerDefaultStoreName(
      store.getters["customer/info"].customerId
    ).then(res => store.commit("customer/setStoreName", res));
  }

  static getCustomerDefaultStoreName(customerId: number) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/default-store-name/customerId/" +
          customerId
      )
      .then(res => res.data);
  }

  static loadCustomerStoreAddresses(store: any) {
    return CustomerService.getCustomerStoreAddresses(
      store.getters["customer/info"].customerId,
      store.getters["customer/info"].defaultStoreId
    ).then(res => {
      store.commit("customer/setStoreAddresses", res);
    });
  }

  static getCustomerStoreAddresses(customerId: number, storeId: number) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/stores/" +
          storeId +
          "/address"
      )
      .then(res => res.data);
  }

  static loadCustomerStoreOpeningHours(store: any) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/stores/business-hours/" +
          store.getters["customer/info"].defaultStoreId
      )
      .then(res => {
        store.commit("customer/setOpeningHours", res.data);
      });
  }

  static loadCustomerStoreInfo(store: any) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/stores/store-with-contacts/" +
          store.getters["customer/info"].defaultStoreId
      )
      .then(res => {
        const keys: ConfigurationDTO[] = res.data.additionalConfig;
        store.commit(
          "updateStoreQaplaEnabled",
          getKeyValueConfig(keys, "QAPLA_ACTIVE")
        );
        store.commit(
          "updateStorePaperlessEnabled",
          getKeyValueConfig(keys, "ENABLE_PAPERLESS")
        );
        store.commit(
          "updateStoreCourierPickupsManagementEnabled",
          getKeyValueConfig(keys, "ENABLE_COURIER_PICKUP_MANAGEMENT")
        );
        store.commit(
          "updateStoreAutopilotEnabled",
          getKeyValueConfig(keys, "ENABLE_AUTOPILOT")
        );
        store.commit("customer/setStoreInfo", res.data);
        store.commit("updateStoreTimeZoneId", res.data.store.zone.timeZoneId);
        store.commit("setCurrency", res.data.store.currency);
      });
  }

  static loadCustomerExtraServiceConfig(store: any) {
    return CustomerService.getCustomerExtraServiceConfig(
      store.getters["storeSelected"].idEntity,
      store.getters["user"].username
    ).then(res => store.commit("customer/setExtraServiceConfig", res));
  }

  static getCustomerExtraServiceConfig(
    customerId: number,
    username: string
  ): Promise<OnlineMBEExtraServiceDto[]> {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/extraserviceconfig/" +
          username
      )
      .then(res => res.data);
  }

  static loadCustomerCourierVisibilityConfig(store: any) {
    return CustomerService.getCustomerCourierVisibilityConfig(
      store.getters["storeSelected"].idEntity,
      store.getters["user"].username
    ).then(res => store.commit("customer/setCourierVisibilityConfig", res));
  }

  static getCustomerCourierVisibilityConfig(
    customerId: number,
    username: string
  ): Promise<OnlineMbeCourierVisibilityDto[]> {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/couriervisibilityconfig/" +
          username
      )
      .then(res => res.data);
  }

  static loadCustomerThermicConfig(store: any) {
    return CustomerService.getCustomerThermicConfig(
      store.getters["storeSelected"].idEntity,
      store.getters["user"].username
    ).then(res => store.commit("customer/setThermicConfig", res));
  }

  static getCustomerThermicConfig(
    customerId: number,
    username: string
  ): Promise<OnlineMBEThermicConfigDto[]> {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/thermicconfig/" +
          username
      )
      .then(res => res.data);
  }

  static loadDefaultShipmentTemplate(
    customerId: number,
    username: string
  ): Promise<OnlineMbeShipmentTemplateDto> {
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/shipmentTemplate/" +
          username
      )
      .then(res => res.data);
  }

  static saveDefaultShipmentTemplate(data: OnlineMbeShipmentTemplateDto) {
    return axios
      .post(
        store.getters.BASE_API_URL +
          "/customers/" +
          data.customerId +
          "/onlinembe/shipmentTemplate/" +
          data.username,
        data
      )
      .then(res => res.data);
  }

  static getCustomerECommerceOrderConfiguration(ecommerceName: string) {
    const customerId = store.getters["customer/info"].customerId;
    const userName = store.getters.user.username;
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/orderconfiguration/" +
          userName,
        {
          params: {
            ecommerceName
          }
        }
      )
      .then(res => res.data);
  }

  static saveCustomerECommerceOrderConfiguration(data) {
    const customerId = store.getters["customer/info"].customerId;
    const userName = store.getters.user.username;
    return axios
      .post(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/orderconfiguration/" +
          userName,
        data
      )
      .then(res => res.data);
  }

  static getCustomerECommerceMbeServices(ecommerceName: string) {
    const customerId = store.getters["customer/info"].customerId;
    const userName = store.getters.user.username;
    return axios
      .get(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/ordermbeservice/" +
          userName,
        {
          params: {
            ecommerceName
          }
        }
      )
      .then(res => res.data);
  }

  static saveCustomerECommerceMbeServices(ecommerceName: string, data) {
    const customerId = store.getters["customer/info"].customerId;
    const userName = store.getters.user.username;
    return axios
      .post(
        store.getters.BASE_API_URL +
          "/customers/" +
          customerId +
          "/onlinembe/ordermbeservice/" +
          userName +
          "?ecommerceName=" +
          ecommerceName,
        data
      )
      .then(res => res.data);
  }

  static getEcommerceServices(ecommerceName: string) {
    return axios
      .get(store.getters.BASE_API_URL + "/shipments/ecommerce-service", {
        params: {
          ecommerceName
        }
      })
      .then(res => res.data);
  }

  getSessionEbayId(customerId: number, username: string) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          `/customers/${customerId}/onlinembe/ebay/login/${username}`
      )
      .then(res => res.data);
  }

  getEbayToken(customerId: number, username: string, sessionId: string) {
    return axios
      .get(
        store.getters.BASE_API_URL +
          `/customers/${customerId}/onlinembe/ebay/fetch_token/${username}`,
        {
          params: { sessionID: sessionId }
        }
      )
      .then(res => res.data);
  }

  static getExtraServiceMaxValue(extraServiceBusinessId: number) {
    if (store.getters["customer/extraServiceConfig"] !== null) {
      const configObject = (store.getters[
        "customer/extraServiceConfig"
      ] as OnlineMBEExtraServiceDto[]).filter(
        item => item.extraServiceId === extraServiceBusinessId
      );
      if (configObject.length === 1) {
        const config = configObject[0];
        return config?.extraServiceMaxValue ? config?.extraServiceMaxValue : 0;
      } else {
        return 0;
      }
    } else {
      return 0;
    }
  }

  getImportDetailErrors(
    storeId: string,
    username: string,
    customerId?: number
  ) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url:
        store.getters.BASE_API_URL + "/customers/import/import-contact/detail",
      params: {
        storeId: storeId,
        username: username,
        sort: "insertDate,desc",
        customerId: customerId
      }
    };
    return axios
      .request(config)
      .then(res => res.data)
      .catch(err => console.error(err.message));
  }

  importCustomer(formData: any, customerId: number, type: string) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/customers/import/import-contact",
      params: {
        customerId: customerId,
        type: type
      },
      data: formData
    };
    return axios
      .request(config)
      .then(res => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static loadClaimConfig(store: any) {
    const customerId = store.getters["storeSelected"].idEntity;
    const molUsername = store.getters["user"].username;
    const config = {
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        `/claim/${customerId}/mbe-online-config/${molUsername}`
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        store.commit("customer/setClaimConfig", res.data);
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }
}
